import { Checkbox, FormControlLabel, Popover, TextField } from '@mui/material';
import { useState } from 'react';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { MAX_TABS, PickableView } from './scheduleTabs';

export interface AddViewPickerProps {
  anchorEl: HTMLElement | null;
  views: PickableView[];
  includedKeys: string[];
  onAdd: (picked: PickableView[]) => void;
  onClose: () => void;
}

export const AddViewPicker = ({ anchorEl, views, includedKeys, onAdd, onClose }: AddViewPickerProps) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const room = MAX_TABS - includedKeys.length;
  const visible = views.filter((view) => view.name.toLowerCase().includes(query.toLowerCase()));

  const close = () => {
    setQuery('');
    setSelected([]);
    onClose();
  };

  const toggle = (viewKey: string) =>
    setSelected((current) =>
      current.includes(viewKey) ? current.filter((key) => key !== viewKey) : [...current, viewKey]
    );

  const add = () => {
    onAdd(selected.flatMap((key) => views.filter((view) => view.viewKey === key)));
    close();
  };

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={close}
      aria-label="Add views to the report"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
      <div
        className="w-full max-w-[22rem] p-3 text-sm"
        data-testid="add-view-picker">
        <TextField
          size="small"
          fullWidth
          placeholder="Search saved views"
          inputProps={{ 'aria-label': 'Search saved views' }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <TextBlock
          size="xs"
          color="grey6"
          className="mt-2">
          Only views saved on this page can be combined.
        </TextBlock>
        <div className="mt-1 max-h-60 overflow-auto">
          {visible.map((view) => {
            const included = includedKeys.includes(view.viewKey);
            const checked = included || selected.includes(view.viewKey);
            const full = !checked && selected.length >= room;
            return (
              <div
                key={view.viewKey}
                className="flex items-center">
                <FormControlLabel
                  sx={{ flexGrow: 1, mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={included || full}
                      onChange={() => toggle(view.viewKey)}
                    />
                  }
                  label={<span className="text-sm">{view.name}</span>}
                />
                {included && <TextSpan color="grey6">Already in report</TextSpan>}
              </div>
            );
          })}
          {visible.length === 0 && (
            <TextBlock
              size="sm"
              color="grey6"
              className="py-3">
              No saved views match.
            </TextBlock>
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          <TextSpan color="grey6">{selected.length} selected</TextSpan>
          <div className="flex gap-2">
            <button
              type="button"
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              disabled={selected.length === 0}
              onClick={add}>
              {selected.length === 0
                ? 'Add views'
                : `Add ${selected.length} ${selected.length === 1 ? 'view' : 'views'}`}
            </button>
          </div>
        </div>
      </div>
    </Popover>
  );
};
